import React, { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";

/**
 * Catches legacy WordPress URLs (and other orphaned paths) and quietly sends
 * the visitor back to the home page. Since we're a SPA behind Cloudflare we
 * can't return a true 301 from this layer, but we do the next-best thing:
 *
 *   • <meta http-equiv="refresh" content="0;url=/"> — tells non-JS crawlers
 *     to move on immediately.
 *   • <link rel="canonical" href="/"> — consolidates SEO equity.
 *   • <meta name="robots" content="noindex"> — stop these orphan URLs from
 *     cluttering the index.
 *   • client-side <Navigate> — the actual browser redirect.
 *
 * Known patterns matched (anywhere in the path):
 *   /wp-admin, /wp-login.php, /xmlrpc.php, /wp-content/…, /wp-includes/…,
 *   /feed, /rss, /rss2, /comments/feed, /author/…, /category/…, /tag/…
 *
 * Query-string legacy patterns (`?page_id=N`, `?p=N`, `?cat=N`) are handled by
 * bucketing the visit straight to `/`.
 */
const LEGACY_PATH_PATTERNS = [
  /^\/wp-admin(\/|$)/i,
  /^\/wp-login\.php$/i,
  /^\/xmlrpc\.php$/i,
  /^\/wp-content(\/|$)/i,
  /^\/wp-includes(\/|$)/i,
  /^\/wp-json(\/|$)/i,
  /^\/feed\/?$/i,
  /^\/rss2?\/?$/i,
  /^\/comments\/feed\/?$/i,
  /^\/author\//i,
  /^\/category\//i,
  /^\/tag\//i,
  /^\/\?page_id=/i,
  /^\/\?p=/i,
];

export function isLegacyWordPressUrl(pathname, search) {
  const full = `${pathname}${search || ""}`;
  return LEGACY_PATH_PATTERNS.some((re) => re.test(full));
}

export default function LegacyRedirect() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.info(
      "[Infinity DMS] Legacy URL hit — redirecting:",
      pathname + (search || "")
    );
  }, [pathname, search]);

  return (
    <>
      <Helmet>
        <title>Redirecting… | Infinity DMS</title>
        <meta httpEquiv="refresh" content="0;url=/" />
        <link rel="canonical" href="https://infinity-dms.co.za/" />
        <meta name="robots" content="noindex,follow" />
      </Helmet>
      <Navigate to="/" replace />
    </>
  );
}
